/**
 * Average video views formatter.
 *
 * `compact` (the default) rounds to whole units, except billions which keep one
 * decimal place - 1104 = 1K, 3_450_000 = 3M, 1_100_000_000 = 1.1B.
 *
 * `compact: false` keeps one decimal place, dropped where the rounded value has
 * no fractional part - 1004 = 1K, 1104 = 1.1K, 3_450_000 = 3.5M.
 *
 * Anything under 1000, negatives included, is written out in full - 999, -5,000.
 */
export function formatAvv(views: number, compact = true): string {
  const wholeViews = Math.round(views) || 0;
  if (wholeViews < 1e3) return wholeViews.toLocaleString('en');

  // Billions keep a decimal place even when compact - 1.1B carries more than 1B.
  const maximumFractionDigits = compact && wholeViews < 1e9 ? 0 : 1;
  // Locale is pinned so the suffixes stay K / M / B - passing undefined would
  // localise them ("1 Mio.") for non-English viewers.
  return wholeViews.toLocaleString('en', { notation: 'compact', maximumFractionDigits });
}

export function pct(n: number): string {
  return `${n >= 0 ? '+' : ''}${n}%`;
}

/** Apply a percentage uplift to a base value. */
export function applyUplift(base: number, upliftPct: number): number {
  return base * (1 + upliftPct / 100);
}

export interface BoldToken {
  bold: boolean;
  text: string;
}

const MARKER = '**';

/**
 * Minimal inline-bold parser for API copy.
 *
 * The API may send content with `**bold**` markers and nothing else,
 * so this handles that one rule rather than pulling in a CommonMark parser.
 *
 * Returns tokens rather than an HTML string so callers render plain React
 * children — copy is API-supplied, and `dangerouslySetInnerHTML`
 * would make it an injection sink.
 *
 * Unpaired or empty markers (`**a`, `****`) are emitted as literal text, so
 * malformed copy degrades to what the API sent rather than throwing or
 * swallowing the rest of the string.
 */
export const parseBold = (content: string) => {
  const tokens: BoldToken[] = [];
  let plainFrom = 0;
  let cursor = 0;

  const pushPlain = (until: number) => {
    if (until > plainFrom) tokens.push({ bold: false, text: content.slice(plainFrom, until) });
  };

  while (cursor < content.length) {
    const open = content.indexOf(MARKER, cursor);
    if (open === -1) break;

    // Nearest following marker, so `**a** and **b**` closes at the second
    // marker rather than spanning the middle.
    const close = content.indexOf(MARKER, open + MARKER.length);
    if (close === -1) break;

    const inner = content.slice(open + MARKER.length, close);
    if (inner === '') {
      // `****` has nothing to embolden — leave it as literal text.
      cursor = close + MARKER.length;
      continue;
    }

    pushPlain(open);
    tokens.push({ bold: true, text: inner });
    cursor = close + MARKER.length;
    plainFrom = cursor;
  }

  pushPlain(content.length);
  return tokens;
};

/** Split the monthly price into units and remainder for display purposes */
export const formatCurrency = (rawAmount: number, currencyCode = 'GBP', options?: Intl.NumberFormatOptions) => {
  const formatted = (rawAmount / 100)
    .toLocaleString('en-gb', {
      currency: currencyCode,
      style: 'currency',
      ...options,
    })
    .replace(/[a-zA-Z]/g, ''); // currencyDisplay isn't widely supported enough, remove alphanumeric characters from response

  const [units, remainder] = formatted.split('.');
  const priceDivider = currencyCode === 'EUR' ? ',' : '.';

  return { units, remainder, priceDivider };
};
